import React, { useState, useRef } from 'react';

interface MessageInputProps {
  onSendMessage: (message: string, replyTo?: any) => void;
  onSendFile: (fileName: string, fileUrl: string, fileSize: number, fileType: string, replyTo?: any) => void;
  replyingTo: any;
  onCancelReply: () => void;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onSendFile,
  replyingTo,
  onCancelReply
}) => {
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    onSendMessage(message.trim(), replyingTo);
    setMessage('');
    autoResize();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('https://api.nephosting.com/upload', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success && data.url) {
        onSendFile(file.name, data.url, file.size, file.type, replyingTo);
      } else {
        alert('File upload failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('File upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="p-4">
      {/* Reply Preview */}
      {replyingTo && (
        <div className="mb-2 p-3 bg-telegram-blue/10 dark:bg-telegram-blue/20 border-l-4 border-telegram-blue rounded-r-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-sm font-medium text-telegram-blue dark:text-blue-400">
                <i className="bi bi-reply me-1"></i>Replying to {replyingTo.username}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300 truncate">{replyingTo.message}</div>
            </div>
            <button 
              onClick={onCancelReply}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <i className="bi bi-x text-gray-500"></i>
            </button>
          </div>
        </div>
      )}

      {/* Message Input Container */}
      <div className="glass border-t border-white/20 dark:border-gray-700/50 rounded-2xl p-4">
        <form onSubmit={handleSubmit} className="flex items-end space-x-3">
          {/* Attachment Button */}
          <button 
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="p-3 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200 flex-shrink-0 disabled:opacity-50" 
            title="Attach file"
          >
            {isUploading ? (
              <i className="bi bi-arrow-clockwise animate-spin text-gray-600 dark:text-gray-300"></i>
            ) : (
              <i className="bi bi-paperclip text-gray-600 dark:text-gray-300"></i>
            )}
          </button>
          <input 
            ref={fileInputRef}
            type="file" 
            className="hidden"
            onChange={handleFileUpload}
          />

          {/* Message Input */}
          <div className="flex-1 relative">
            <textarea 
              ref={textareaRef}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                autoResize();
              }}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl resize-none focus:ring-2 focus:ring-telegram-blue focus:border-transparent outline-none transition-all duration-200 text-gray-800 dark:text-white placeholder-gray-400 max-h-32" 
              placeholder="Type a message..." 
              rows={1}
            />
          </div>

          {/* Send Button */}
          <button 
            type="submit"
            disabled={!message.trim()}
            className="p-3 rounded-full bg-telegram-blue hover:bg-blue-600 transition-colors duration-200 flex-shrink-0 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" 
            title="Send message"
          >
            <i className="bi bi-send text-white"></i>
          </button>
        </form>
      </div>
    </div>
  );
};

export default MessageInput;